// 後台「使用熱力分析」—— 站內使用熱點（Phase 1-A）
//   資料來自 GET /api/admin/analytics/usage（聚合 pageview + auditlog），全自家資料、無外部套件。
import { useState, useEffect, useMemo, Fragment } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import { TextField, Box } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "AuthContext";
import { COLORS } from "theme/tokens";

const DOW_LABELS = ["", "日", "一", "二", "三", "四", "五", "六"]; // MySQL DAYOFWEEK 1=日 … 7=六
const DOWS = [1, 2, 3, 4, 5, 6, 7];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const isoDaysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

// 2 碼國家代碼 → 國旗 emoji（A→🇦 …）
const flagEmoji = (cc) =>
  cc && /^[A-Za-z]{2}$/.test(cc)
    ? String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))
    : "🏳️";

function StatTile({ label, value }) {
  return (
    <Card sx={{ p: 2.5, textAlign: "center" }}>
      <MDTypography variant="h3" fontWeight="bold" color="info">
        {value === undefined || value === null ? "—" : value}
      </MDTypography>
      <MDTypography variant="button" color="text">{label}</MDTypography>
    </Card>
  );
}

function RankList({ items }) {
  const max = items.reduce((m, it) => Math.max(m, it.count), 0) || 1;
  if (!items.length) return <MDTypography variant="button" color="text">無資料</MDTypography>;
  return (
    <Box display="flex" flexDirection="column" gap={0.9}>
      {items.map((it, i) => (
        <Box key={i}>
          <Box display="flex" justifyContent="space-between" sx={{ fontSize: 12.5 }}>
            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "78%" }} title={it.label}>
              {i + 1}. {it.label}
            </Box>
            <Box sx={{ fontWeight: 600, color: COLORS.grayDark }}>{it.count}</Box>
          </Box>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: "#eef0f3", mt: "3px" }}>
            <Box sx={{ height: 4, borderRadius: 2, width: `${Math.round((it.count / max) * 100)}%`, bgcolor: "#ff8a3d" }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function UsageAnalytics() {
  const { token } = useAuth();
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  const [data, setData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set("from", new Date(`${from}T00:00:00`).toISOString());
      if (to) qs.set("to", new Date(`${to}T23:59:59.999`).toISOString());
      const headers = { Authorization: `Bearer ${token}` };
      const [uRes, gRes, mRes] = await Promise.all([
        fetch(`/api/admin/analytics/usage?${qs.toString()}`, { headers }),
        fetch(`/api/admin/analytics/geo?${qs.toString()}`, { headers }),
        fetch(`/api/admin/analytics/market`, { headers }),
      ]);
      const uD = await uRes.json();
      if (uRes.ok) setData(uD);
      else { setData(null); window.alert(uD?.message || "讀取失敗"); }
      const gD = await gRes.json();
      setGeoData(gRes.ok ? gD : null);
      const mD = await mRes.json();
      setMarketData(mRes.ok ? mD : null);
    } catch (e) {
      setData(null); setGeoData(null); setMarketData(null);
      console.error("fetch analytics error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const { heatMap, heatMax } = useMemo(() => {
    const m = {}; let max = 0;
    (data?.heatmap || []).forEach((h) => {
      m[`${h.dow}-${h.hour}`] = h.count;
      if (h.count > max) max = h.count;
    });
    return { heatMap: m, heatMax: max };
  }, [data]);

  const cellColor = (c) => {
    if (!c) return "#f4f5f7";
    const t = heatMax ? c / heatMax : 0;
    return `rgba(255, 138, 61, ${0.15 + t * 0.85})`; // 由淺到主色橘
  };

  const num = (v) => (typeof v === "number" ? v.toLocaleString() : v);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* 標題 + 時間區間 */}
        <Card sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <MDTypography variant="h5" fontWeight="bold">使用熱力分析</MDTypography>
            <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
              <TextField type="date" label="起" InputLabelProps={{ shrink: true }} size="small" value={from} onChange={(e) => setFrom(e.target.value)} />
              <TextField type="date" label="迄" InputLabelProps={{ shrink: true }} size="small" value={to} onChange={(e) => setTo(e.target.value)} />
              <MDButton color="info" variant="gradient" size="small" onClick={fetchData} disabled={loading}>
                {loading ? "查詢中..." : "查詢"}
              </MDButton>
            </Box>
          </Box>
        </Card>

        {/* KPI */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}><StatTile label="頁面瀏覽次數" value={num(data?.totals?.pageViews)} /></Grid>
          <Grid item xs={12} md={4}><StatTile label="功能動作次數" value={num(data?.totals?.actions)} /></Grid>
          <Grid item xs={12} md={4}><StatTile label="活躍使用者" value={num(data?.totals?.activeUsers)} /></Grid>
        </Grid>

        {/* 星期 × 小時 熱力圖 */}
        <Card sx={{ p: 2, mt: 3 }}>
          <MDTypography variant="h6" fontWeight="bold" mb={1.5}>活躍熱力圖（星期 × 小時，台北時間）</MDTypography>
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "34px repeat(24, minmax(20px, 1fr))", gap: "2px", minWidth: 640 }}>
              <Box />
              {HOURS.map((h) => (
                <Box key={`h${h}`} sx={{ fontSize: 10, textAlign: "center", color: COLORS.grayDark }}>{h}</Box>
              ))}
              {DOWS.map((dw) => (
                <Fragment key={`row-${dw}`}>
                  <Box sx={{ fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.grayDark }}>{DOW_LABELS[dw]}</Box>
                  {HOURS.map((h) => {
                    const c = heatMap[`${dw}-${h}`] || 0;
                    return <Box key={`${dw}-${h}`} title={`週${DOW_LABELS[dw]} ${h}:00 · ${c} 次`} sx={{ height: 18, borderRadius: "2px", bgcolor: cellColor(c) }} />;
                  })}
                </Fragment>
              ))}
            </Box>
          </Box>
        </Card>

        {/* 三張排行 */}
        <Grid container spacing={3} mt={0.5}>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={1.5}>最熱頁面</MDTypography>
              <RankList items={(data?.topPages || []).map((p) => ({ label: p.path, count: p.count }))} />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={1.5}>功能使用排行</MDTypography>
              <RankList items={(data?.topActions || []).map((a) => ({ label: a.action, count: a.count }))} />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={1.5}>最活躍使用者</MDTypography>
              <RankList items={(data?.topUsers || []).map((u) => ({ label: u.email, count: u.count }))} />
            </Card>
          </Grid>
        </Grid>

        {/* 登入地理分佈（geoip-lite 離線定位） */}
        <Grid container spacing={3} mt={0.5}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={1.5}>登入國家分佈</MDTypography>
              <RankList items={(geoData?.countries || []).map((c) => ({ label: `${flagEmoji(c.label)} ${c.label}`, count: c.count }))} />
              {geoData?.unresolved ? (
                <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                  另有 {geoData.unresolved} 筆無法定位（內網 / localhost / 未知 IP）
                </MDTypography>
              ) : null}
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={1.5}>登入城市分佈</MDTypography>
              <RankList items={(geoData?.cities || []).map((c) => ({ label: c.label, count: c.count }))} />
            </Card>
          </Grid>
        </Grid>

        {/* 市場/交易熱點（精靈採用熱度，目前累計） */}
        <MDBox mt={3} mb={1}>
          <MDTypography variant="h6" fontWeight="bold">交易 / 商品熱點</MDTypography>
          <MDTypography variant="caption" color="text" sx={{ display: "block" }}>
            精靈策略採用（目前累計）：採用中 {marketData?.totals?.activeAdoptions ?? "—"} · 總採用 {marketData?.totals?.adoptions ?? "—"} · 菁英庫 {marketData?.totals?.eliteCount ?? "—"} 支
          </MDTypography>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={1.5}>最熱門商品</MDTypography>
              <RankList items={(marketData?.topSymbols || []).map((s) => ({ label: s.symbol, count: s.count }))} />
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={1.5}>最多人採用的策略</MDTypography>
              <RankList items={(marketData?.topElites || []).map((e) => ({ label: e.name, count: e.count }))} />
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
